import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Building, GraduationCap, MapPin, Save, Trash2, AlertTriangle, ExternalLink } from 'lucide-react';
import Footer from '@/components/Footer';
import WeeklyAlertsConfig from '@/components/WeeklyAlertsConfig';
import InstantNotificationsConfig from '@/components/InstantNotificationsConfig';
import { AreaAutocomplete } from '@/components/AreaSelector';

const KNOWLEDGE_AREAS = [
  'Ciências Exatas e da Terra',
  'Ciências Biológicas',
  'Engenharias',
  'Ciências da Saúde',
  'Ciências Agrárias',
  'Ciências Sociais Aplicadas',
  'Ciências Humanas',
  'Linguística, Letras e Artes',
  'Multidisciplinar'
];

const GENDER_OPTIONS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'feminino', label: 'Feminino' },
  { value: 'outro', label: 'Outro' },
  { value: 'prefiro_nao_informar', label: 'Prefiro não informar' }
];

const AGE_OPTIONS = [
  { value: '18-24', label: '18-24 anos' },
  { value: '25-34', label: '25-34 anos' },
  { value: '35-44', label: '35-44 anos' },
  { value: '45-54', label: '45-54 anos' },
  { value: '55-64', label: '55-64 anos' },
  { value: '65+', label: '65+ anos' }
];

const TITLE_OPTIONS = [
  { value: 'graduando', label: 'Graduando(a)' },
  { value: 'graduado', label: 'Graduado(a)' },
  { value: 'especializando', label: 'Especializando(a)' },
  { value: 'especialista', label: 'Especialista' },
  { value: 'mestrando', label: 'Mestrando(a)' },
  { value: 'mestre', label: 'Mestre' },
  { value: 'doutorando', label: 'Doutorando(a)' },
  { value: 'doutor', label: 'Doutor(a)' },
  { value: 'pos_doutor', label: 'Pós-Doutor(a)' }
];

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    institution: '',
    title: '',
    lattes_url: '',
    google_scholar_url: '',
    gender: '',
    age: '',
    city: '',
    state: '',
    knowledge_areas: []
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/user/profile');
      setProfile({
        name: response.data.name || '',
        email: response.data.email || '',
        institution: response.data.institution || '',
        title: response.data.title || '',
        lattes_url: response.data.lattes_url || '',
        google_scholar_url: response.data.google_scholar_url || '',
        gender: response.data.gender || '',
        age: response.data.age || '',
        city: response.data.city || '',
        state: response.data.state || '',
        knowledge_areas: response.data.knowledge_areas || []
      });
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAreaToggle = (area) => {
    setProfile(prev => ({
      ...prev,
      knowledge_areas: prev.knowledge_areas.includes(area)
        ? prev.knowledge_areas.filter(a => a !== area)
        : [...prev.knowledge_areas, area]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: profile.name,
        institution: profile.institution,
        title: profile.title,
        lattes_url: profile.lattes_url,
        google_scholar_url: profile.google_scholar_url,
        gender: profile.gender,
        age: profile.age,
        city: profile.city,
        state: profile.state,
        knowledge_areas: profile.knowledge_areas
      };

      await api.put('/api/user/profile', updateData);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'EXCLUIR') {
      toast.error('Digite EXCLUIR para confirmar');
      return;
    }

    try {
      await api.delete('/api/user/account');
      toast.success('Conta excluída com sucesso');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir conta');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Meu Perfil</h1>
            <p className="text-sm text-gray-500">Gerencie seus dados pessoais</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        <div className="space-y-6">
          
          {/* Basic Info Card */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <User className="h-5 w-5 text-[#002FA7]" />
              <h2 className="text-lg font-semibold">Informações Básicas</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  data-testid="profile-name"
                  value={profile.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="email"
                    value={profile.email}
                    disabled
                    className="bg-gray-50"
                  />
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado</p>
              </div>

              <div>
                <Label htmlFor="gender">Gênero</Label>
                <select
                  id="gender"
                  data-testid="profile-gender"
                  value={profile.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
                >
                  <option value="">Selecione...</option>
                  {GENDER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="age">Faixa Etária</Label>
                <select
                  id="age"
                  data-testid="profile-age"
                  value={profile.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
                >
                  <option value="">Selecione...</option>
                  {AGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Academic Info Card */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <GraduationCap className="h-5 w-5 text-[#002FA7]" />
              <h2 className="text-lg font-semibold">Informações Acadêmicas</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="institution">Instituição</Label>
                <Input
                  id="institution"
                  data-testid="profile-institution"
                  value={profile.institution}
                  onChange={(e) => handleChange('institution', e.target.value)}
                  placeholder="Ex: Universidade de São Paulo"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="title">Titulação</Label>
                <select
                  id="title"
                  data-testid="profile-title"
                  value={profile.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
                >
                  <option value="">Selecione...</option>
                  {TITLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="lattes">Currículo Lattes</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="lattes"
                    data-testid="profile-lattes"
                    value={profile.lattes_url}
                    onChange={(e) => handleChange('lattes_url', e.target.value)}
                    placeholder="http://lattes.cnpq.br/..."
                  />
                  {profile.lattes_url && (
                    <a href={profile.lattes_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-5 w-5 text-[#002FA7]" />
                    </a>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="scholar">Google Scholar</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="scholar"
                    data-testid="profile-scholar"
                    value={profile.google_scholar_url}
                    onChange={(e) => handleChange('google_scholar_url', e.target.value)}
                    placeholder="https://scholar.google.com/..."
                  />
                  {profile.google_scholar_url && (
                    <a href={profile.google_scholar_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-5 w-5 text-[#002FA7]" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Knowledge Areas - Updated with Autocomplete */}
            <div className="mt-6">
              <AreaAutocomplete
                value={profile.knowledge_areas}
                onChange={(areas) => handleChange('knowledge_areas', areas)}
                label="Áreas de Interesse (Específicas)"
                maxSelections={5}
                placeholder="Digite para buscar áreas específicas (ex: sociologia, química...)"
              />
              <p className="text-xs text-gray-500 mt-2">
                Selecione até 5 áreas específicas do conhecimento para receber chamadas mais relevantes.
              </p>
            </div>
          </div>

          {/* Instant Notifications Card */}
          <InstantNotificationsConfig token={localStorage.getItem('auth_token')} />

          {/* Weekly Alerts Card */}
          <WeeklyAlertsConfig token={localStorage.getItem('auth_token')} />

          {/* Location Card */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="h-5 w-5 text-[#002FA7]" />
              <h2 className="text-lg font-semibold">Localização</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  data-testid="profile-city"
                  value={profile.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Ex: São Paulo"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="state">Estado</Label>
                <select
                  id="state"
                  data-testid="profile-state"
                  value={profile.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
                >
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map(st => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#002FA7] hover:bg-[#001f75]"
              data-testid="save-profile-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>

          {/* Danger Zone */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Zona de Perigo</h2>
            </div>

            <p className="text-sm text-red-700 mb-4">
              A exclusão da conta é permanente e não pode ser desfeita. Todos os seus dados, favoritos e alertas serão removidos.
            </p>

            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-100"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Minha Conta
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="confirm-delete" className="text-red-700">
                    Digite EXCLUIR para confirmar
                  </Label>
                  <Input
                    id="confirm-delete"
                    data-testid="delete-confirm-input"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="EXCLUIR"
                    className="mt-1 border-red-300"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'EXCLUIR'}
                    data-testid="confirm-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Confirmar Exclusão
                  </Button>
                </div>
              </div>
            )}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
